// API service for Sterling app
// Handles API calls for both development and production environments

const getApiBaseUrl = () => {
  // In development, use localhost
  if (process.env.NODE_ENV === 'development') {
    return process.env.REACT_APP_API_URL || 'http://localhost:8000';
  }
  
  // In production, use relative URLs (Vercel handles routing)
  return '';
};

const API_BASE_URL = getApiBaseUrl();

class ApiService {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  // Helper method to get full URL
  getUrl(endpoint) {
    return `${this.baseUrl}${endpoint}`;
  }

  // Helper method to get auth headers
  getAuthHeaders() {
    const token = localStorage.getItem('access_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }

  // Generic request method
  async request(endpoint, options = {}) {
    const url = this.getUrl(endpoint);
    const headers = {
      'Content-Type': 'application/json',
      ...this.getAuthHeaders(),
      ...options.headers
    };

    const config = {
      ...options,
      headers
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Auth endpoints
  async login(credentials) {
    return this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials)
    });
  }

  async register(userData) {
    return this.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }

  async refreshToken() {
    return this.request('/api/auth/refresh', {
      method: 'POST'
    });
  }

  // User endpoints
  async getCurrentUser() {
    return this.request('/api/users/me');
  }

  async updateUser(userData) {
    return this.request('/api/users/me', {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
  }

  // Chat endpoints
  async sendChatQuery(query, conversation_id = null) {
    const body = { query };
    if (conversation_id) body.conversation_id = conversation_id;
    return this.request('/api/chat/query', {
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  // Streaming chat endpoint for thinking process
  async sendChatQueryStream(query, conversation_id = null, onUpdate = null) {
    const body = { query };
    if (conversation_id) body.conversation_id = conversation_id;
    
    const token = localStorage.getItem('access_token');
    const url = this.getUrl('/api/chat/query/stream');
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    while (true) {
      const { done, value } = await reader.read();
      
      if (done) {
        break;
      }

      const chunk = decoder.decode(value);
      const lines = chunk.split('\n');
      
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            if (onUpdate) {
              onUpdate(data);
            }
          } catch (e) {
            console.error('Error parsing stream data:', e);
          }
        }
      }
    }
  }

  // Conversation history endpoints
  async getConversations() {
    console.log('API: Getting conversations...');
    const result = await this.request('/api/chat/conversations');
    console.log('API: Conversations result:', result);
    return result;
  }

  async getConversation(conversationId) {
    return this.request(`/api/chat/conversations/${conversationId}`);
  }

  async createConversation(title) {
    return this.request('/api/chat/conversations', {
      method: 'POST',
      body: JSON.stringify({ title })
    });
  }

  async deleteConversation(conversationId) {
    return this.request(`/api/chat/conversations/${conversationId}`, {
      method: 'DELETE'
    });
  }

  async getConversationMessages(conversationId, limit = 50, offset = 0) {
    return this.request(`/api/chat/conversations/${conversationId}/messages?limit=${limit}&offset=${offset}`);
  }

  async regenerateMessage(conversationId, messageId) {
    return this.request(`/api/chat/conversations/${conversationId}/regenerate`, {
      method: 'POST',
      body: JSON.stringify({ message_id: messageId })
    });
  }

  // QuickBooks endpoints
  async getQuickBooksStatus() {
    return this.request('/api/quickbooks/status');
  }

  async connectQuickBooks() {
    return this.request('/api/quickbooks/connect');
  }

  async disconnectQuickBooks() {
    return this.request('/api/quickbooks/disconnect', {
      method: 'DELETE'
    });
  }

  // Document endpoints
  async uploadDocument(file) {
    const formData = new FormData();
    formData.append('file', file);
    
    const token = localStorage.getItem('access_token');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    
    const url = this.getUrl('/api/documents/upload');
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData
    });
    
    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }
    
    return response.json();
  }

  async getDocuments() {
    return this.request('/api/documents');
  }

  // Structured Query endpoint for new backend
  async sendStructuredQuery(query) {
    return this.request('/api/query', {
      method: 'POST',
      body: JSON.stringify({ query })
    });
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;

// Also export individual methods for convenience
export const {
  login,
  register,
  refreshToken,
  getCurrentUser,
  updateUser,
  getQuickBooksStatus,
  connectQuickBooks,
  disconnectQuickBooks,
  uploadDocument,
  getDocuments,
  sendChatQuery,
  sendChatQueryStream,
  getConversations,
  getConversation,
  createConversation,
  deleteConversation,
  getConversationMessages,
  sendStructuredQuery
} = apiService; 